import { useTranslation } from "react-i18next";
import {
  Category,
  convertCategoryEntityToCategory,
} from "../../../domain/model/Category";
import { Game, GameData, TeamTurns } from "../../../domain/model/Game";
import { getLifelines } from "../../../domain/model/Lifeline";
import { Difficulty } from "../../../domain/model/Question";
import { useNavigate } from "react-router-dom";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { PlayerGetCurrentGameUseCase } from "../../../domain/usecase/player/GetCurrentGames/GetCurrentGamesUseCase";
import { State } from "../../../domain/model/ResponseState";

export function PlayedGamesViewModel(
  playerGetCurrentGameUseCase: PlayerGetCurrentGameUseCase
) {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [games, setGames] = useState<Array<Game>>([]);

  useEffect(() => {
    getGames();
  }, []);

  async function getGames() {
    setLoading(true);
    const response = await playerGetCurrentGameUseCase.invoke();
    switch (response.responseState) {
      case State.Success:
        if (response.data) {
          setGames(response.data);
        }
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    setLoading(false);
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" = "error"
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }

  function onGameClicked(game: Game) {
    const categories: Array<Category> = [];
    game.selectedCategories.map((selectedCategory) => {
      const ce = selectedCategory.category;

      const subcategories = ce.subcategories?.filter((_, index) =>
        selectedCategory.selectedSubcategoriesIndexes.includes(index)
      );
      ce.subcategories = subcategories;
      const category = convertCategoryEntityToCategory(ce);
      category.subcategories = category.subcategories.map((s) => {
        s.isSelected = true;
        return s;
      });
      categories.push(category);
    });

    const difficultiesMap: Map<Difficulty, number> = new Map([
      [Difficulty.EASY, game.difficultiesMap?.easy ?? 60],
      [Difficulty.MEDIUM, game.difficultiesMap?.medium ?? 60],
      [Difficulty.HARD, game.difficultiesMap?.hard ?? 60],
    ]);

    var firstTeamLifelines = getLifelines(t).slice(0, 3);
    var secondTeamLifelines = getLifelines(t).slice(0, 3);

    if (
      game.firstTeamLifelinesList != null &&
      game.firstTeamLifelinesList!.length > 0
    ) {
      firstTeamLifelines = getLifelines(t).filter((l) =>
        game.firstTeamLifelinesList!.includes(l.type)
      );
    }

    if (
      game.secondTeamLifelinesList != null &&
      game.secondTeamLifelinesList!.length > 0
    ) {
      secondTeamLifelines = getLifelines(t).filter((l) =>
        game.secondTeamLifelinesList!.includes(l.type)
      );
    }

    const gameData: GameData = {
      resultGame: game,
      categories: categories,
      firstTeamName: game.firstTeamName ?? "NAMELESS 1",
      secondTeamName: game.secondTeamName ?? "NAMELESS 2",
      firstTeamScore: 0,
      secondTeamScore: 0,
      firstTeamLifelines,
      secondTeamLifelines,
      difficultiesMap,
      shouldPlaySounds: game.shouldPlaySounds ?? true,
      canAnyTeamChooseAnyQuestion: game.canAnyTeamChooseAnyQuestion ?? false,
      currentTeamTurn: TeamTurns.teamA,
      isLoading: false,
    };
    navigate(ROUTE_ENDPOINTS.GAME, { state: gameData, replace: true });
  }

  return {
    onGameClicked,
    loading,
    games,
  };
}
